import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Database, Upload, ChevronRight, User as UserIcon, FileText } from 'lucide-react';
import { listImportSessions, type ImportSessionSummary } from '@/api/import-history';
import { SESSION_STATUS_META, formatDateTime } from '@/lib/import-history-labels';

interface DataImportsCardProps {
  schoolCode: string;
  /** Lien vers l'assistant d'import V2 (bouton affiché seulement si fourni). */
  importV2Url?: string;
}

/**
 * Bloc « Données » de la fiche école : historique complet des imports
 * réalisés via l'assistant (qui, quand, volumes). Chaque ligne ouvre la page
 * de détail de l'import (trace élément par élément + fichiers sources).
 */
export function DataImportsCard({ schoolCode, importV2Url }: DataImportsCardProps) {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState<ImportSessionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listImportSessions(schoolCode)
      .then((items) => {
        if (!cancelled) setSessions(items);
      })
      .catch(() => {
        if (!cancelled) setError("Impossible de charger l'historique des imports.");
      });
    return () => {
      cancelled = true;
    };
  }, [schoolCode]);

  return (
    <Card>
      <CardHeader
        icon={<Database />}
        iconColor="teal"
        actions={
          importV2Url ? (
            <Button variant="default" size="sm" asChild>
              <Link to={importV2Url}>
                <Upload className="h-4 w-4 mr-1" />
                Importer des données
              </Link>
            </Button>
          ) : undefined
        }
      >
        <CardTitle>Données</CardTitle>
      </CardHeader>
      <CardContent>
        {error && <p className="text-sm text-muted-foreground">{error}</p>}
        {!error && sessions === null && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}
        {!error && sessions !== null && sessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-6 text-center text-muted-foreground">
            <FileText className="h-8 w-8 mb-2 opacity-40" />
            <p className="text-sm">Aucun import réalisé pour le moment.</p>
            {importV2Url && (
              <p className="text-xs mt-1">
                Utilisez « Importer des données » pour alimenter l'école depuis des fichiers.
              </p>
            )}
          </div>
        )}
        {!error && sessions !== null && sessions.length > 0 && (
          <div className="divide-y divide-border/60">
            {sessions.map((session) => {
              const status = SESSION_STATUS_META[session.status] ?? SESSION_STATUS_META.running;
              return (
                <button
                  key={session.id}
                  type="button"
                  onClick={() => navigate(`/schools/${schoolCode}/imports/${session.id}`)}
                  className="flex w-full items-center gap-3 py-3 text-left transition-colors hover:bg-muted/40 rounded-md px-2 -mx-2"
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">
                        Import du {formatDateTime(session.startedAt)}
                      </p>
                      <Badge variant="outline" className={`border-0 ${status.className}`}>
                        {status.label}
                      </Badge>
                    </div>
                    <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                      <UserIcon className="h-3 w-3" />
                      {session.createdBy || 'Inconnu'}
                      {session.targetSchoolPeriodLabel && (
                        <span>· {session.targetSchoolPeriodLabel}</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold">
                      {session.totalItems.toLocaleString('fr-FR')}{' '}
                      <span className="font-normal text-muted-foreground">données</span>
                    </p>
                    {session.failedCount > 0 && (
                      <p className="text-xs text-red-600">
                        {session.failedCount.toLocaleString('fr-FR')} en échec
                      </p>
                    )}
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                </button>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
